// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import { get } from "@insite/client-framework/Services/ApiService";
import { GetShipTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import {
    BillToModel,
    PaginationModel,
    ShipToCollectionModel,
    ShipToModel,
} from "@insite/client-framework/Types/ApiModels";

const shipTosUrl = "api/cmi/shipTos";

export interface CmiShipToDto extends ShipToModel {
    parentId?: string | null;
    countryName?: string | null;
    stateName?: string | null;
    stateAbbreviation?: string | null;
}

export interface CmiShipToCollectionApiResult {
    shipToCollection?: CmiShipToDto[];
    page?: number;
    pageSize?: number;
    defaultPageSize?: number;
    totalPages?: number;
    totalCount?: number;
}

type CustomerAddress = ShipToModel | BillToModel;

function isBlankSequence(sequence?: string | null) {
    return !sequence || sequence.trim() === "" || sequence === "0";
}

export function getShipToParentBillToId(shipTo?: ShipToModel): string | undefined {
    if (!shipTo) {
        return undefined;
    }

    const parentId = (shipTo as CmiShipToDto).parentId || shipTo.properties?.parentId;
    if (parentId && parentId.toLowerCase() !== emptyGuid) {
        return parentId;
    }

    return shipTo.id;
}

function mapToBillTo(customer: CustomerAddress, id: string): BillToModel {
    const billTo = customer as BillToModel;
    return {
        uri: customer.uri || "",
        properties: customer.properties || {},
        id,
        accountsReceivable: billTo.accountsReceivable ?? null,
        address1: customer.address1 || "",
        address2: customer.address2 || "",
        address3: customer.address3 || "",
        address4: customer.address4 || "",
        attention: customer.attention || "",
        budgetEnforcementLevel: billTo.budgetEnforcementLevel || "",
        city: customer.city || "",
        companyName: customer.companyName || customer.customerName || "",
        contactFullName: customer.contactFullName || "",
        costCodes: billTo.costCodes ?? null,
        costCodeTitle: billTo.costCodeTitle || "",
        country: customer.country,
        customerCurrencySymbol: billTo.customerCurrencySymbol || "",
        customerName: customer.customerName || customer.companyName || "",
        customerNumber: customer.customerNumber || "",
        customerSequence: customer.customerSequence || "",
        email: customer.email || "",
        fax: customer.fax || "",
        firstName: customer.firstName || "",
        fullAddress: customer.fullAddress || customer.label || "",
        isDefault: !!customer.isDefault,
        isGuest: !!billTo.isGuest,
        label: customer.label || "",
        lastName: customer.lastName || "",
        phone: customer.phone || "",
        postalCode: customer.postalCode || "",
        shipTos: billTo.shipTos ?? null,
        shipTosUri: billTo.shipTosUri || "",
        state: customer.state,
        validation: customer.validation,
    };
}

export function getAssociatedBillTo(
    shipTo?: ShipToModel,
    relatedCustomers: CustomerAddress[] = [],
): BillToModel | undefined {
    if (!shipTo) {
        return undefined;
    }

    const parentId = getShipToParentBillToId(shipTo);
    const parentIdLower = parentId?.toLowerCase();
    const customerNumber = (shipTo.customerNumber || "").trim();

    const related = relatedCustomers.find(customer => {
        if (parentIdLower && customer.id?.toLowerCase() === parentIdLower) {
            return true;
        }

        return (
            !!customerNumber && customer.customerNumber === customerNumber && isBlankSequence(customer.customerSequence)
        );
    });

    const source = related || (parentIdLower && shipTo.id.toLowerCase() === parentIdLower ? shipTo : undefined);
    if (!source || !parentId) {
        return undefined;
    }

    return mapToBillTo(source, related?.id || parentId);
}

function mapCmiShipTo(shipTo: CmiShipToDto): CmiShipToDto {
    const parentId = shipTo.parentId && shipTo.parentId.toLowerCase() !== emptyGuid ? shipTo.parentId : "";

    const state =
        shipTo.state ||
        (shipTo.stateAbbreviation || shipTo.stateName
            ? {
                  uri: "",
                  properties: {},
                  id: "",
                  abbreviation: shipTo.stateAbbreviation || "",
                  name: shipTo.stateName || "",
              }
            : null);

    const country =
        shipTo.country ||
        (shipTo.countryName
            ? {
                  uri: "",
                  properties: {},
                  id: "",
                  abbreviation: "",
                  name: shipTo.countryName,
                  states: null,
              }
            : null);

    return {
        ...shipTo,
        uri: shipTo.uri || "",
        properties: {
            ...(shipTo.properties || {}),
            parentId,
        },
        parentId,
        address1: shipTo.address1 || "",
        address2: shipTo.address2 || "",
        address3: shipTo.address3 || "",
        address4: shipTo.address4 || "",
        attention: shipTo.attention || "",
        city: shipTo.city || "",
        companyName: shipTo.companyName || "",
        contactFullName: shipTo.contactFullName || [shipTo.firstName, shipTo.lastName].filter(Boolean).join(" "),
        country,
        customerName: shipTo.customerName || shipTo.companyName || "",
        customerNumber: shipTo.customerNumber || "",
        customerSequence: shipTo.customerSequence || "",
        email: shipTo.email || "",
        fax: shipTo.fax || "",
        firstName: shipTo.firstName || "",
        lastName: shipTo.lastName || "",
        fullAddress: shipTo.fullAddress || shipTo.label || "",
        isDefault: !!shipTo.isDefault,
        isNew: !!shipTo.isNew,
        isVmiLocation: !!shipTo.isVmiLocation,
        label: shipTo.label || "",
        oneTimeAddress: !!shipTo.oneTimeAddress,
        phone: shipTo.phone || "",
        postalCode: shipTo.postalCode || "",
        state,
        validation: shipTo.validation || null,
    };
}

function mapPagination(result: CmiShipToCollectionApiResult): PaginationModel {
    return {
        currentPage: result.page ?? 1,
        page: result.page ?? 1,
        pageSize: result.pageSize ?? 20,
        defaultPageSize: result.defaultPageSize ?? 20,
        totalItemCount: result.totalCount ?? 0,
        numberOfPages: result.totalPages ?? 0,
        pageSizeOptions: [],
        sortOptions: [],
        sortType: "",
        nextPageUri: "",
        prevPageUri: "",
    };
}

export async function xGetShipTos(parameter: GetShipTosApiParameter): Promise<ShipToCollectionModel> {
    const result = await get<CmiShipToCollectionApiResult>(shipTosUrl, parameter);
    const shipTos = (result.shipToCollection || []).map(mapCmiShipTo);

    return {
        uri: shipTosUrl,
        properties: {},
        shipTos,
        pagination: mapPagination(result),
    };
}
